"use client";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import Image from "next/image";
import { useEffect, useState } from "react";
import { useSession } from "next-auth/react";

import { SvgIcon, SvgIconsEnums } from "@ilg-credability/credability-web-components";

import styles from "./Navigation.module.css";
import { MenuOverlayNav } from "./MenuOverlayNav";
import { Analytics } from "@/utilities/analytics";
import { MainCategoryEnum, PageEnum } from "@/types/Analytics";
import { env } from "next-runtime-env";
import { onLogout } from "@/services/auth";

const NEXT_PUBLIC_NEXTAUTH_DOB_CLIENTID = env('NEXT_PUBLIC_NEXTAUTH_DOB_CLIENTID')

export const NavigationBar = ({
  menuOpened,
  menuClosed,
}: {
  menuOpened: () => void;
  menuClosed: () => void;
}) => {
  const currentRoute = usePathname();
  const router = useRouter();
  const { status, data: session } = useSession()

  const [showMenuOverlay, setShowMenuOverlay] = useState(false);

  const onClose = (targetScreen?: PageEnum) => {
    closeMenu();
    targetScreen && fireEvent(targetScreen);
  };

  const onOpen = () => {
    document.body.style.overflowY = "hidden";
    setShowMenuOverlay(true);
    menuOpened();
    router.push("#header");
  };

  useEffect(() => {
    closeMenu();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRoute]);

  const closeMenu = () => {
    setShowMenuOverlay(false);
    menuClosed();
    document.body.style.overflowY = "scroll";
  };

  // this is conditional class to hide the menu on mobile only for app-exclusives page
  const hideMenuMobile =
    currentRoute.includes("app-exclusives") ||
      currentRoute.includes("settings/delete-account")
      ? "tablet-down-hide"
      : "";

  const handleClick = () => {
    let memberType = "credit_score_set";
    if (
      sessionStorage.getItem("targetScreen") === "where_is_my_report_thin_file"
    ) {
      memberType = "thin_file";
    } else if (
      sessionStorage.getItem("targetScreen") === "where_is_my_report_kba_fail"
    ) {
      memberType = "kba_file";
    }

    Analytics.logEvent("cta_get_the_app_start", {
      action: "get_the_app",
      main_category: MainCategoryEnum.MENU,
      referral_screen: "dashboard_get_the_app",
      member_type: memberType,
    });
  };

  const fireEvent = (targetScreen: PageEnum) => {
    Analytics.logEvent("cta_from_menu", {
      action: "cta_from_menu",
      main_category: MainCategoryEnum.MENU,
      target_screen: targetScreen,
      referral_screen: "menu",
    });
  };

  return (
    <div>
      <header className={styles.navHeader}>
        <div className={`${styles.navHeaderContainer} ${hideMenuMobile}`}>
          {session?.provider === NEXT_PUBLIC_NEXTAUTH_DOB_CLIENTID ? <Image
            className="tablet-down-hide credability-logo"
            src="/logo.svg"
            alt="CredAbility Logo"
            width={144}
            height={32}
            priority={true}
            onClick={() => onLogout(session?.idToken)}
          /> :
            <Link href={"/"}>
              <Image
                className="tablet-down-hide"
                src="/logo.svg"
                alt="CredAbility Logo"
                width={144}
                height={32}
                priority={true}
              />
            </Link>}
          {/* navigation is hidden if user not authorised AND route is not 'sorry-to-see-you-go' (after user deletes account) */}
          {status === "authenticated" && !currentRoute.includes("sorry-to-see-you-go") && session?.provider !== NEXT_PUBLIC_NEXTAUTH_DOB_CLIENTID && (
            <nav className={styles.navigation} data-testid="navigation">
              <ul className={styles.navigationList}>
                {/* Main Pages */}
                <li
                  className={`${styles.navigationListItem} ${(currentRoute.includes("dashboard") ||
                    currentRoute === "/") &&
                    !showMenuOverlay
                    ? styles.navigationListItemActive
                    : ""
                    }`}
                >
                  <Link href="/" onClick={() => onClose()} className={`${styles.navigationLinkCore} ${(currentRoute.includes("dashboard") ||
                    currentRoute === "/") &&
                    !showMenuOverlay
                    ? styles.active
                    : ""
                    }`}>
                    <SvgIcon
                      icon={SvgIconsEnums.LAYOUT}
                      size={{ height: 24 }}
                      fill={
                        (currentRoute.includes("dashboard") ||
                          currentRoute === "/") &&
                          !showMenuOverlay
                          ? 'var(--cred-color-light-primary)'
                          : 'var(--cred-color-light-grey)'
                      }
                      loading={false}
                    />
                    Dashboard
                  </Link>
                </li>
                <li
                  id="report-nav"
                  className={`${styles.navigationListItem} ${currentRoute.includes("report") && !showMenuOverlay
                    ? styles.navigationListItemActive
                    : ""
                    }`}
                >
                  <Link href="/report" onClick={() => onClose()} className={`${styles.navigationLinkCore} ${currentRoute.includes("report") && !showMenuOverlay
                    ? styles.active
                    : ""
                    }`}>
                    <SvgIcon
                      icon={SvgIconsEnums.DOCUMENT}
                      size={{ height: 24 }}
                      fill={
                        currentRoute.includes("report") && !showMenuOverlay
                          ? 'var(--cred-color-light-primary)'
                          : 'var(--cred-color-light-grey)'
                      }
                      loading={false}
                    />
                    Report
                  </Link>
                </li>

                <li
                  id="deals-nav"
                  className={`${styles.navigationListItem} ${currentRoute.includes("deals") && !showMenuOverlay
                    ? styles.navigationListItemActive
                    : ""
                    }`}
                >
                  <Link href="/deals" onClick={() => onClose()} className={`${styles.navigationLinkCore} ${currentRoute.includes("deals") && !showMenuOverlay
                    ? styles.active
                    : ""
                    }`}>
                    <SvgIcon
                      icon={SvgIconsEnums.FIRE}
                      size={{ height: 24 }}
                      fill={
                        currentRoute.includes("deals") && !showMenuOverlay
                          ? 'var(--cred-color-light-primary)'
                          : 'var(--cred-color-light-grey)'
                      }
                      loading={false}
                    />
                    Deals
                  </Link>
                </li>

                {/* APP Link */}
                <li className={`${styles.navigationListItem} ${styles.ctaWithGradient}`}>
                  <Link href="/app-exclusives" onClick={handleClick}>
                    Get the app
                  </Link>
                </li>

                {/* Settings Page */}
                <li
                  className={`${styles.navigationListItem} ${currentRoute.includes("settings") ? styles.navigationListItemActive : ""
                    }`}
                >
                  <button
                    onClick={onOpen}
                    type="button"
                    className={`${styles.menuLink} menu-link
                      ${showMenuOverlay ? styles.active : ""}`}
                  >
                    <SvgIcon
                      icon={SvgIconsEnums.MENU}
                      size={{ height: 24 }}
                      fill={
                        showMenuOverlay
                          ? 'var(--cred-color-light-primary)'
                          : 'var(--cred-color-light-grey)'
                      }
                      loading={false}
                    />
                    Menu
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </div>
      </header>
      {status === "authenticated" && session?.provider !== NEXT_PUBLIC_NEXTAUTH_DOB_CLIENTID && (
        <MenuOverlayNav isActive={showMenuOverlay} onClose={onClose} />
      )}
    </div>
  );
};
